import type { CertificateDto, PromotionCandidateDto } from '@bjj/shared';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useParams } from 'react-router';
import { api } from '../api/client';
import { useApiMutation } from '../api/hooks';
import {
  BeltSwatch,
  CertificateView,
  SignatureCard,
  useSignatureStatus,
} from '../components/Certificates';
import { DataTable } from '../components/DataTable';
import {
  Card,
  ConfirmButton,
  ErrorMessage,
  formatDate,
  Loading,
  PageHeader,
} from '../components/ui';

/** Students ready for their next belt, and the signature instructors sign certificates with. */
export function PromotionsPage() {
  const candidates = useQuery({
    queryKey: ['promotions'],
    queryFn: () =>
      api.request<{ items: PromotionCandidateDto[] }>('/promotions').then((r) => r.items),
  });
  const signature = useSignatureStatus();
  const [done, setDone] = useState<CertificateDto | null>(null);
  const issue = useApiMutation<string, { certificate: CertificateDto }>(
    (studentId) => ({ path: '/certificates', method: 'POST', body: { studentId } }),
    [['promotions'], ['students'], ['certificates']],
  );
  const canSign = signature.data?.hasSignature ?? false;
  return (
    <>
      <PageHeader
        title="Promotions"
        subtitle="Students who have mastered every technique in their curriculum. Issuing a certificate records their new belt."
      />
      <ErrorMessage error={candidates.error ?? issue.error} />
      {done ? (
        <div className="alert success" role="status">
          {done.studentName} was promoted to {done.beltName}.{' '}
          <Link to={`/certificates/${done.id}`}>View and print the certificate</Link>
        </div>
      ) : null}
      <Card title="Ready for promotion">
        {signature.data && !canSign ? (
          <div className="alert info" role="status">
            Add your signature below to issue certificates.
          </div>
        ) : null}
        <DataTable
          rows={candidates.data}
          rowKey={(c) => c.user.id}
          empty="Nobody is waiting: students show here once they've mastered their whole curriculum."
          columns={[
            {
              key: 'name',
              header: 'Student',
              render: (c) => <Link to={`/students/${c.user.id}`}>{c.user.name}</Link>,
            },
            {
              key: 'belt',
              header: 'Next belt',
              render: (c) => (
                <span>
                  <BeltSwatch belt={c.nextBelt.belt} /> {c.nextBelt.name}
                </span>
              ),
            },
            { key: 'done', header: 'Finished', render: (c) => formatDate(c.completedAt) },
            {
              key: 'actions',
              header: '',
              render: (c) => (
                <ConfirmButton
                  confirmLabel={`Promote ${c.user.name} and sign?`}
                  className="btn primary small"
                  disabled={!canSign || issue.isPending}
                  onConfirm={() =>
                    issue.mutate(c.user.id, { onSuccess: (r) => setDone(r.certificate) })
                  }
                >
                  Issue certificate
                </ConfirmButton>
              ),
            },
          ]}
        />
      </Card>
      <SignatureCard />
    </>
  );
}

export function CertificatePage() {
  const { id } = useParams();
  const cert = useQuery({
    queryKey: ['certificate', id],
    queryFn: () =>
      api
        .request<{ certificate: CertificateDto }>(`/certificates/${id}`)
        .then((r) => r.certificate),
  });
  if (cert.isLoading) return <Loading />;
  if (cert.error || !cert.data)
    return <ErrorMessage error={cert.error ?? new Error('Not found')} />;
  return (
    <>
      <PageHeader
        title={`${cert.data.beltName}: ${cert.data.studentName}`}
        subtitle={`Issued ${formatDate(cert.data.issuedAt)} by ${cert.data.issuerName}`}
        actions={
          <>
            <Link className="btn" to={`/students/${cert.data.studentId}`}>
              Back to {cert.data.studentName}
            </Link>
            <button type="button" className="btn primary" onClick={() => window.print()}>
              Print
            </button>
          </>
        }
      />
      <CertificateView certificate={cert.data} />
    </>
  );
}
